import { useState, type ReactNode } from "react"
import type { OrderStatus } from "@/components/chat/OrderStatusLink"
import OrderDetailsDialog, {
  classifyExportStatus,
} from "@/components/maps/orderDetails/OrderDetailsDialog"
import { useLatestDeliveryOrder } from "@/hooks/useLatestDeliveryOrder"
import type { RouteStop } from "@/reducers/routes2gSlice"

/**
 * The overview bar's "Order status" link, wired to the real order: the
 * same latest-order read and the same OrderDetailsDialog the classic map's
 * order button (DeliveryOrderExportStatusCard) uses. Returns the link's
 * data for DriverOverviewBar's `orderStatus` prop (undefined off SLT, or
 * with no stops) and the dialog to render next to it.
 */
export const useOrderStatusLink = (
  driverJid: string | undefined,
  stops: RouteStop[] | undefined,
): { orderStatus?: OrderStatus; dialog: ReactNode } => {
  const [open, setOpen] = useState(false)
  const { enabled, latest } = useLatestDeliveryOrder(driverJid, stops, open)

  if (!enabled || !driverJid) return { orderStatus: undefined, dialog: null }

  const hasOrder = Boolean(latest?.id)
  return {
    orderStatus: {
      status: hasOrder
        ? classifyExportStatus(latest?.exportStatus ?? "")
        : "noOrder",
      exportId: latest?.exportId,
      onOpen: () => setOpen(true),
    },
    dialog: hasOrder ? (
      <OrderDetailsDialog
        driverJid={driverJid}
        deliveryOrderId={latest!.id}
        isOpen={open}
        onClose={() => setOpen(false)}
      />
    ) : null,
  }
}
